/**
 * backorder-refresh.js
 * Keeps the stored Backorder/Preorder line item property on each cart line in
 * sync with the live product metafield, so the value carried into checkout, the
 * order, and order emails reflects the CURRENT expected ship date rather than
 * whatever was captured when the item was first added to the cart.
 *
 * Desired state per line is rendered server-side into .bp-reconcile spans by
 * snippets/preorder-backorder-state.liquid (single source of truth, mirrors
 * snippets/buy-buttons.liquid). This script compares that to the stored cart
 * properties and rewrites them via /cart/change.js when they differ.
 *
 * On page load the rendered DOM is already current, so the DOM hooks are used.
 * On cart open and checkout the DOM may be stale (the drawer is not re-rendered
 * on open), so a fresh cart-drawer section is fetched and reconciled against.
 *
 * Lines are matched by line item key, falling back to line position. Clearing
 * uses property removal, then retries by blanking the value (which the cart
 * template and checkout both hide) if change.js ignores the empty object.
 *
 * NOTE: console.warn diagnostics below are temporary for debugging and should
 * be removed before publish.
 *
 * Triggers: initial page load, cart drawer open, and checkout click.
 */
(function () {
  'use strict';

  var TAG = '[bp-refresh]';
  function routes() { return window.routes || null; }
  var running = false;

  function collectDesired(root) {
    root = root || document;
    var byKey = {}, byLine = {}, count = 0;
    var nodes = root.querySelectorAll('.bp-reconcile');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var d = {
        name: el.getAttribute('data-bp-name') || '',
        value: el.getAttribute('data-bp-value') || ''
      };
      var key = el.getAttribute('data-bp-key');
      var line = el.getAttribute('data-bp-line');
      if (key) byKey[key] = d;
      if (line) byLine[line] = d;
      count++;
    }
    return { byKey: byKey, byLine: byLine, count: count };
  }

  // Fetch a fresh cart-drawer section and read desired state from it, so cart
  // open / checkout reflect admin changes made after the page was loaded.
  function freshDesired() {
    var r = routes();
    return fetch(r.cart_url + '?section_id=cart-drawer', { headers: { Accept: 'text/html' } })
      .then(function (res) { return res.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        return collectDesired(doc);
      })
      .catch(function () { return collectDesired(document); });
  }

  function hasFlag(props) {
    props = props || {};
    return Object.prototype.hasOwnProperty.call(props, 'Backorder') ||
           Object.prototype.hasOwnProperty.call(props, 'Preorder');
  }

  function isInSync(item, d) {
    var p = item.properties || {};
    var hasB = Object.prototype.hasOwnProperty.call(p, 'Backorder');
    var hasP = Object.prototype.hasOwnProperty.call(p, 'Preorder');
    if (d.name === 'Backorder') return hasB && !hasP && p.Backorder === d.value;
    if (d.name === 'Preorder') return hasP && !hasB && p.Preorder === d.value;
    return !hasB && !hasP;
  }

  function buildProps(item, d, blankClear) {
    var out = {}, src = item.properties || {};
    for (var k in src) {
      if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
      if (k === 'Backorder' || k === 'Preorder') continue;
      out[k] = src[k];
    }
    if (d.name === 'Backorder') out.Backorder = d.value;
    else if (d.name === 'Preorder') out.Preorder = d.value;
    else if (blankClear) {
      if (Object.prototype.hasOwnProperty.call(src, 'Backorder')) out.Backorder = '';
      if (Object.prototype.hasOwnProperty.call(src, 'Preorder')) out.Preorder = '';
    }
    return out;
  }

  function change(lineIndex, item, props) {
    var r = routes();
    var body = { line: lineIndex, quantity: item.quantity, properties: props };
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      body.selling_plan = item.selling_plan_allocation.selling_plan.id;
    }
    return fetch(r.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) { return res.json(); });
  }

  function reconcile(desired) {
    var r = routes();
    if (!r || !r.cart_change_url || !r.cart_url) return Promise.resolve(false);
    if (running) return Promise.resolve(false);
    if (!desired) desired = collectDesired(document);
    if (!desired.count) return Promise.resolve(false);

    running = true;
    return fetch(r.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var jobs = [];
        for (var i = 0; i < items.length; i++) {
          var lineIndex = i + 1;
          var d = desired.byKey[items[i].key] || desired.byLine[String(lineIndex)];
          if (!d) {
            if (hasFlag(items[i].properties)) {
              console.warn(TAG, 'line', lineIndex, 'has a flag but no desired match; key=', items[i].key);
            }
            continue;
          }
          if (!isInSync(items[i], d)) jobs.push({ lineIndex: lineIndex, item: items[i], d: d });
        }
        if (!jobs.length) return false;
        console.warn(TAG, jobs.length, 'line(s) to reconcile');

        var chain = Promise.resolve();
        jobs.forEach(function (job) {
          chain = chain.then(function () {
            return change(job.lineIndex, job.item, buildProps(job.item, job.d, false)).then(function (cart2) {
              var updated = (cart2 && cart2.items && cart2.items[job.lineIndex - 1]) || null;
              if (job.d.name === '' && updated && hasFlag(updated.properties)) {
                console.warn(TAG, 'empty clear ignored on line', job.lineIndex, '- retrying with blank values');
                return change(job.lineIndex, job.item, buildProps(job.item, job.d, true));
              }
              return cart2;
            });
          });
        });
        return chain.then(function () { return true; });
      })
      .catch(function (e) { console.warn(TAG, 'error', e); return false; })
      .then(function (changed) { running = false; return changed; });
  }

  function publishCartUpdate() {
    var r = routes();
    return fetch(r.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'backorder-refresh', cartData: cart });
        }
        return true;
      })
      .catch(function () { return true; });
  }

  function reconcileAndRender(desired) {
    return reconcile(desired).then(function (changed) {
      return changed ? publishCartUpdate() : false;
    });
  }

  // Trigger: initial page load (DOM is freshly rendered).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcileAndRender(); });
  } else {
    reconcileAndRender();
  }

  // Trigger: cart drawer opened via the header cart icon (fetch fresh state).
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('#cart-icon-bubble')) {
      freshDesired().then(function (d) { return reconcileAndRender(d); });
    }
  });

  // Trigger: checkout. Fetch fresh state, sync, then hand off. Never block the
  // button for more than ~2s.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('#CartDrawer-Checkout, #checkout, button[name="checkout"]');
    if (!btn) return;

    var form = btn.form || (btn.closest ? btn.closest('form') : null);
    var done = false;
    var proceed = function () {
      if (done) return;
      done = true;
      if (form && typeof form.requestSubmit === 'function') form.requestSubmit(btn);
      else if (form) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden'; hidden.name = 'checkout'; hidden.value = '';
        form.appendChild(hidden); form.submit();
      } else window.location.href = '/checkout';
    };

    e.preventDefault();
    var timer = setTimeout(proceed, 2000);
    freshDesired()
      .then(function (d) { return reconcile(d); })
      .then(function () { clearTimeout(timer); proceed(); })
      .catch(function () { clearTimeout(timer); proceed(); });
  }, true);
})();
